import { cva } from 'class-variance-authority';
import clsx from 'clsx';
import React from 'react';
import { twMerge } from 'tailwind-merge';

import type { IButtonProps } from './IButtonProps';

export const buttonStyles = cva(
  'w-full rounded-xl gap-2 font-semibold inline-flex items-center p-[10px] justify-center max-w-full',
  {
    variants: {
      intent: {
        hero: 'md:h-16 h-14 bg-retail-primary-700 hover:bg-retail-primary-600 disabled:opacity-50 focus:ring-retail-primary-900 focus:ring-1 active:bg-retail-primary-800 text-white md:text-lg text-base',
        primary:
          'md:h-14 h-12 bg-retail-primary-700 hover:bg-retail-primary-600 disabled:opacity-50 focus:outline-retail-primary-900 focus:outline-2 active:bg-retail-primary-800 text-white text-sm md:text-base',
        secondary:
          'md:h-14 h-12 bg-white text-retail-primary-700 text-sm md:text-base border-2 border-retail-primary-700 hover:border-retail-primary-600 hover:text-retail-primary-600 active:border-retail-primary-800 active:text-retail-primary-800 focus:outline-2 focus:outline-retail-primary-900 disabled:opacity-50',
        tertiary:
          'md:text-lg h-10 md:h-12 text-base !p-0 text-retail-primary-700 max-w-max bg-none gap-2 hover:text-retail-primary-600 active:text-retail-primary-800 focus:text-retail-primary-700 disabled:opacity-50 focus:ring-retail-primary-900 data-[loading]:bg',
        red: 'md:h-14 h-12 text-base text-white bg-primary-500 gap-2 pressed: caret-mobile-primary-600 active:text-white focus:text-primary-700 disabled:opacity-50 focus:ring-primary-900 data-[loading]:bg hover:text-white  ',
        redSecondary:
          'md:h-14 h-12 text-base text-primary-500 border-2 border-primary-500 gap-2  active:text-white active:bg-primary-500 focus:text-primary-700 disabled:opacity-50 focus:ring-primary-900 data-[loading]:bg',
      },
      defaultVariants: {
        intent: 'primary',
      },
    },
  },
);

const Button = React.forwardRef<HTMLButtonElement, IButtonProps>(
  (
    {
      children,
      className,
      spinnerClassName = '',
      intent = 'primary',
      prefixIcon: PrefixIcon,
      trailingIcon: TrailingIcon,
      loading,
      loadingText,
      ...rest
    },
    forwardRef,
  ) => {
    return (
      <button
        type="button"
        className={twMerge(buttonStyles({ intent }), className)}
        ref={forwardRef}
        data-loading={loading}
        {...rest}
      >
        {loading ? (
          <>
            <span
              aria-live="polite"
              className={twMerge(
                clsx({
                  'inline-block bg-transparent h-6 w-6 border-transparent animate-spin rounded-full border-2 align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]':
                    true,
                  'border-t-retail-primary-600 border-l-retail-primary-600 border-r-retail-primary-600':
                    intent === 'secondary' || intent === 'tertiary',
                  'border-t-white border-l-white border-r-white':
                    intent === 'primary' || intent === 'hero',
                }),
                spinnerClassName,
              )}
              role="status"
            />
            {loadingText ? <span>{loadingText}</span> : null}
          </>
        ) : (
          <>
            {PrefixIcon ? <span>{PrefixIcon}</span> : null}
            {children}
            {TrailingIcon ? <span>{TrailingIcon}</span> : null}
          </>
        )}
      </button>
    );
  },
);
Button.displayName = 'Button';

export default Button;
